import { BaseCurrentPage } from "@/infrastructure/BasePage/basePage.component";
import { RoleName } from "@/types";

// Centralized role-based permissions configuration
export const PAGE_PERMISSIONS: Record<RoleName, BaseCurrentPage[]> = {
  super_admin: [
    "dashboard",
    "leads",
    "quotation",
    "reportsList",
    "orderTracking",
    "item",
    "userManagement",
    "adminManagement",
    "profile",
    "notFound",
    "mergeCompanies",
  ],
  admin: [
    "dashboard",
    "leads",
    "quotation",
    "reportsList",
    "orderTracking",
    "item",
    "userManagement",
    "profile",
    "notFound",
    "mergeCompanies",
  ],
  approved_user: [
    "dashboard",
    "leads",
    "quotation",
    "reportsList",
    "orderTracking",
    "item",
    "profile",
    "notFound",
  ],
  new_user: ["notFound"],
  blocked_user: ["notFound"],
};

/**
 * Check if a user role has permission to access a specific page
 * @param role - The user's role
 * @param page - The page to check access for
 * @returns boolean indicating if access is allowed
 */
export const checkPageAccess = (
  role: RoleName,
  page: BaseCurrentPage
): boolean => {
  // Get allowed pages for the user role
  const allowedPages = PAGE_PERMISSIONS[role] || [];

  // Check if the current page is in the allowed pages list
  return allowedPages.includes(page);
};

/**
 * Check if a role has administrative privileges
 * @param role - The user's role to check
 * @returns boolean indicating if the role has admin privileges
 */
export const hasAdminPrivileges = (role: RoleName): boolean => {
  return role === "admin" || role === "super_admin";
};

/**
 * Check if a role has super admin privileges
 * @param role - The user's role to check
 * @returns boolean indicating if the role has super admin privileges
 */
export const hasSuperAdminPrivileges = (role: RoleName): boolean => {
  return role === "super_admin";
};

/**
 * Get user-friendly display name for a role
 * @param role - The role to get display name for
 * @returns string representing the user-friendly role name
 */
export const getRoleDisplayName = (role: RoleName): string => {
  const roleDisplayNames: Record<RoleName, string> = {
    super_admin: "Super Admin",
    admin: "Admin",
    approved_user: "Approved User",
    new_user: "New User",
    blocked_user: "Blocked User",
  };

  return roleDisplayNames[role] || role;
};
